import { useState, useEffect, useRef } from 'react';
import { FiPlus, FiUpload, FiDownload, FiAlertTriangle, FiXCircle } from 'react-icons/fi';
import api from '../../utils/api';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';
import Badge from '../../components/Badge';
import DepartmentSelect from '../../components/DepartmentSelect';
import { formatDate, getCertStatus } from '../../utils/dateHelpers';
import { exportToExcel } from '../../utils/exportUtils';
const LOCATIONS = ['NH', 'AHC', 'Spoke Clinic'];

export default function StaffTable() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [importModal, setImportModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [filters, setFilters] = useState({ department: '', location: '', status: '' });
  const [subView, setSubView] = useState(null); // null, 'near-expiry', 'expired'
  const [subData, setSubData] = useState([]);
  const fileRef = useRef();

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.department) params.set('department', filters.department);
      if (filters.location) params.set('location', filters.location);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      const data = await api.get(`/staff${qs ? '?' + qs : ''}`);
      setStaff(data.staff || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, [filters]);

  const fetchSubView = async (type) => {
    try {
      const data = await api.get(`/staff/${type}`);
      setSubData(data.staff || []);
      setSubView(type);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editItem?.employee_id) {
        await api.put(`/staff/${editItem.employee_id}`, formData);
      } else {
        await api.post('/staff', formData);
      }
      setEditModal(false);
      setEditItem(null);
      fetchStaff();
    } catch (err) {
      alert(err.message || 'Failed to save');
    }
  };

  const handleDelete = async (row) => {
    if (!confirm(`Delete ${row.staff_name}?`)) return;
    try {
      await api.delete(`/staff/${row.employee_id}`);
      fetchStaff();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleImport = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const resp = await fetch('/api/v1/staff/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('ata_token')}` },
        body: formData,
      });
      const data = await resp.json();
      setImportResult(data);
      fetchStaff();
    } catch (err) {
      setImportResult({ message: 'Import failed: ' + err.message });
    }
  };

  const handleExport = () => {
    const rows = staff.map((s) => ({
      'Employee ID': s.employee_id,
      'Name': s.staff_name,
      'Department': s.department,
      'Position': s.position,
      'Location': s.location,
      'Phone': s.phone,
      'Email': s.email,
      'BLS Issue': s.bls_issue_date,
      'BLS Expiry': s.bls_expiry_date,
      'BLS Status': s.bls_expiry_date ? getCertStatus(s.bls_expiry_date) : '',
      'ACLS Issue': s.acls_issue_date,
      'ACLS Expiry': s.acls_expiry_date,
      'ACLS Status': s.acls_expiry_date ? getCertStatus(s.acls_expiry_date) : '',
      'ATLS Issue': s.atls_issue_date,
      'ATLS Expiry': s.atls_expiry_date,
      'ATLS Status': s.atls_expiry_date ? getCertStatus(s.atls_expiry_date) : '',
      'NRP Issue': s.nrp_issue_date,
      'NRP Expiry': s.nrp_expiry_date,
      'NRP Status': s.nrp_expiry_date ? getCertStatus(s.nrp_expiry_date) : '',
      'Grand Rounds': s.grand_round_count,
    }));
    exportToExcel(rows, 'staff_export');
  };

  const columns = [
    { key: 'employee_id', label: 'Employee ID' },
    { key: 'staff_name', label: 'Name' },
    { key: 'department', label: 'Department' },
    { key: 'position', label: 'Position' },
    { key: 'location', label: 'Location' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    {
      key: 'bls_expiry_date', label: 'BLS',
      render: (val, row) => row.bls_issue_date ? (
        <div>
          <div style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>{formatDate(row.bls_issue_date)}</div>
          <Badge status={getCertStatus(val)} text={`${getCertStatus(val)} - ${formatDate(val)}`} />
        </div>
      ) : <span style={{ color: '#B2AB9C' }}>N/A</span>,
    },
    {
      key: 'acls_expiry_date', label: 'ACLS',
      render: (val, row) => row.acls_issue_date ? (
        <div>
          <div style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>{formatDate(row.acls_issue_date)}</div>
          <Badge status={getCertStatus(val)} text={`${getCertStatus(val)} - ${formatDate(val)}`} />
        </div>
      ) : <span style={{ color: '#B2AB9C' }}>N/A</span>,
    },
    {
      key: 'atls_expiry_date', label: 'ATLS',
      render: (val, row) => row.atls_issue_date ? (
        <div>
          <div style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>{formatDate(row.atls_issue_date)}</div>
          <Badge status={getCertStatus(val)} text={`${getCertStatus(val)} - ${formatDate(val)}`} />
        </div>
      ) : <span style={{ color: '#B2AB9C' }}>N/A</span>,
    },
    {
      key: 'nrp_expiry_date', label: 'NRP',
      render: (val, row) => row.nrp_issue_date ? (
        <div>
          <div style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>{formatDate(row.nrp_issue_date)}</div>
          <Badge status={getCertStatus(val)} text={`${getCertStatus(val)} - ${formatDate(val)}`} />
        </div>
      ) : <span style={{ color: '#B2AB9C' }}>N/A</span>,
    },
    { key: 'grand_round_count', label: 'Grand Rounds' },
  ];

  return (
    <div>
      {/* Action Bar */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <DepartmentSelect value={filters.department} onChange={(v) => setFilters({ ...filters, department: v })} placeholder="All Departments" style={{ width: '180px' }} />
            <select value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} style={{ width: '160px' }}>
              <option value="">All Locations</option>
              {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button className="btn btn-sm btn-secondary" onClick={() => fetchSubView('near-expiry')}>
              <FiAlertTriangle size={14} /> Near Expiry
            </button>
            <button className="btn btn-sm btn-secondary" style={{ borderColor: '#C0392B', color: '#C0392B' }} onClick={() => fetchSubView('expired')}>
              <FiXCircle size={14} /> Expired
            </button>
            <button className="btn btn-sm btn-secondary" onClick={() => setImportModal(true)}>
              <FiUpload size={14} /> Import
            </button>
            <button className="btn btn-sm btn-secondary" onClick={handleExport}>
              <FiDownload size={14} /> Export
            </button>
            <button className="btn btn-sm btn-primary" onClick={() => { setEditItem(null); setEditModal(true); }}>
              <FiPlus size={14} /> Add Staff
            </button>
          </div>
        </div>
      </div>

      {/* Sub View for Near Expiry / Expired */}
      {subView && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <h4>{subView === 'near-expiry' ? 'Certificates Expiring Within 3 Months' : 'Expired Certificates'}</h4>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-sm btn-secondary" onClick={() => {
                const rows = subData.map((s) => ({
                  'Employee ID': s.employee_id,
                  'Name': s.staff_name,
                  'Department': s.department,
                  'Flagged Certs': (s.near_expiry_certs || s.expired_certs || []).join(', '),
                  'BLS Expiry': s.bls_expiry_date,
                  'ACLS Expiry': s.acls_expiry_date,
                  'ATLS Expiry': s.atls_expiry_date,
                  'NRP Expiry': s.nrp_expiry_date,
                }));
                exportToExcel(rows, subView === 'near-expiry' ? 'near_expiry_report' : 'expired_report');
              }}>
                <FiDownload size={14} /> Export
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => setSubView(null)}>Close</button>
            </div>
          </div>
          <DataTable
            columns={[
              { key: 'employee_id', label: 'ID' },
              { key: 'staff_name', label: 'Name' },
              { key: 'department', label: 'Department' },
              {
                key: subView === 'near-expiry' ? 'near_expiry_certs' : 'expired_certs',
                label: 'Flagged Certs',
                render: (val) => (val || []).map((c) => (
                  <Badge key={c} status={subView === 'near-expiry' ? 'expiring' : 'expired'} text={c} />
                )),
              },
              { key: 'bls_expiry_date', label: 'BLS Expiry', render: (v) => formatDate(v) },
              { key: 'acls_expiry_date', label: 'ACLS Expiry', render: (v) => formatDate(v) },
              { key: 'atls_expiry_date', label: 'ATLS Expiry', render: (v) => formatDate(v) },
              { key: 'nrp_expiry_date', label: 'NRP Expiry', render: (v) => formatDate(v) },
            ]}
            data={subData}
          />
        </div>
      )}

      {/* Main Staff Table */}
      {loading ? (
        <div className="card text-center p-4"><div className="spinner" style={{ margin: '40px auto' }} /></div>
      ) : (
        <DataTable
          columns={columns}
          data={staff}
          onEdit={(row) => { setEditItem(row); setEditModal(true); }}
          onDelete={handleDelete}
        />
      )}

      {/* Edit/Add Modal */}
      <Modal isOpen={editModal} onClose={() => { setEditModal(false); setEditItem(null); }} title={editItem ? 'Edit Staff Member' : 'Add Staff Member'}>
        <StaffForm staff={editItem} onSave={handleSave} onCancel={() => { setEditModal(false); setEditItem(null); }} />
      </Modal>

      {/* Import Modal */}
      <Modal isOpen={importModal} onClose={() => { setImportModal(false); setImportResult(null); }} title="Import Staff from Excel/CSV">
        <div>
          <p className="text-sm text-muted mb-3">Upload an Excel (.xlsx) or CSV file with columns: Employee ID, Name, Department, Position, Location, Phone, Email, BLS Issue, BLS Expiry, etc.</p>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ marginBottom: '16px' }} />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary" onClick={handleImport}>Upload & Import</button>
            <button className="btn btn-secondary" onClick={() => { setImportModal(false); setImportResult(null); }}>Cancel</button>
          </div>
          {importResult && (
            <div style={{ marginTop: '16px', padding: '12px', borderRadius: '8px', backgroundColor: importResult.imported > 0 ? '#e8f0e5' : '#fde8e8' }}>
              <p className="text-sm font-medium">{importResult.message}</p>
              {importResult.errors?.length > 0 && (
                <ul style={{ marginTop: '8px', fontSize: '0.8rem', color: '#C0392B' }}>
                  {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  {importResult.errors.length > 5 && <li>...and {importResult.errors.length - 5} more errors</li>}
                </ul>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function StaffForm({ staff, onSave, onCancel }) {
  const [form, setForm] = useState({
    employee_id: staff?.employee_id || '',
    staff_name: staff?.staff_name || '',
    department: staff?.department || '',
    position: staff?.position || '',
    location: staff?.location || '',
    phone: staff?.phone || '',
    email: staff?.email || '',
    bls_issue_date: staff?.bls_issue_date || '',
    bls_expiry_date: staff?.bls_expiry_date || '',
    acls_issue_date: staff?.acls_issue_date || '',
    acls_expiry_date: staff?.acls_expiry_date || '',
    atls_issue_date: staff?.atls_issue_date || '',
    atls_expiry_date: staff?.atls_expiry_date || '',
    nrp_issue_date: staff?.nrp_issue_date || '',
    nrp_expiry_date: staff?.nrp_expiry_date || '',
  });

  const set = (k, v) => {
    const updated = { ...form, [k]: v };
    // Auto-calculate expiry when issue date changes
    if (k.endsWith('_issue_date') && v) {
      const prefix = k.replace('_issue_date', '');
      const d = new Date(v);
      d.setFullYear(d.getFullYear() + 2);
      updated[`${prefix}_expiry_date`] = d.toISOString().split('T')[0];
    }
    setForm(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-row">
        <div className="form-group">
          <label>Employee ID *</label>
          <input value={form.employee_id} onChange={(e) => set('employee_id', e.target.value)} required disabled={!!staff} />
        </div>
        <div className="form-group">
          <label>Staff Name *</label>
          <input value={form.staff_name} onChange={(e) => set('staff_name', e.target.value)} required />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Department</label>
          <DepartmentSelect value={form.department} onChange={(v) => set('department', v)} />
        </div>
        <div className="form-group">
          <label>Position</label>
          <input value={form.position} onChange={(e) => set('position', e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Location</label>
          <select value={form.location} onChange={(e) => set('location', e.target.value)}>
            <option value="">Select...</option>
            {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
        </div>
      </div>
      <div className="form-group">
        <label>Email</label>
        <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
      </div>

      <h4 style={{ margin: '20px 0 12px', fontSize: '0.95rem' }}>Certifications</h4>
      {['bls', 'acls', 'atls', 'nrp'].map((cert) => (
        <div className="form-row" key={cert}>
          <div className="form-group">
            <label>{cert.toUpperCase()} Issue Date</label>
            <input type="date" value={form[`${cert}_issue_date`]} onChange={(e) => set(`${cert}_issue_date`, e.target.value)} />
          </div>
          <div className="form-group">
            <label>{cert.toUpperCase()} Expiry Date</label>
            <input type="date" value={form[`${cert}_expiry_date`]} onChange={(e) => set(`${cert}_expiry_date`, e.target.value)} />
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary">Save</button>
      </div>
    </form>
  );
}
